// Template Generators Index
// Export all template generators from a single entry point

import { 
  generateProfessionalClassicDOCX, 
  generateProfessionalClassicPDF,
  generateProfessionalClassicHTML
} from './professionalClassic';
import { generateATSOptimizedDOCX, generateATSOptimizedPDF, generateATSOptimizedHTML } from './atsOptimized';
import { generateExecutiveDOCX, generateExecutivePDF, generateExecutiveHTML } from './executiveTemplate';

export type TemplateGeneratorFn = (resumeData: any) => void | Promise<void>;

export interface TemplateGenerators {
  generatePDF: TemplateGeneratorFn;
  generateDOCX: TemplateGeneratorFn;
}

const sanitizeStringForDownload = (value: string): string => {
  return value
    .replace(/%PDF-/g, ' ')
    .replace(/stream/g, ' ')
    .replace(/endobj/g, ' ')
    .replace(/endstream/g, ' ')
    .replace(/[ --]/g, ' ')
    .replace(/ /g, ' ')
    .trim();
};

const sanitizeResumeDataForDownload = (data: any): any => {
  if (data === null || data === undefined) return data;
  if (typeof data === 'string') return sanitizeStringForDownload(data);
  if (typeof data === 'number' || typeof data === 'boolean') return data;
  if (Array.isArray(data)) return data.map(sanitizeResumeDataForDownload);
  if (typeof data === 'object') {
    return Object.fromEntries(
      Object.entries(data).map(([key, value]) => [key, sanitizeResumeDataForDownload(value)])
    );
  }
  return data;
};

const templateGenerators: Record<string, TemplateGenerators> = {
  'professional-classic': {
    generatePDF: generateProfessionalClassicPDF,
    generateDOCX: generateProfessionalClassicDOCX,
  },
  'ats-optimized': {
    generatePDF: generateATSOptimizedPDF,
    generateDOCX: generateATSOptimizedDOCX,
  },
  'executive': {
    generatePDF: generateExecutivePDF,
    generateDOCX: generateExecutiveDOCX,
  },
};

export function getTemplateGenerator(templateId: string): TemplateGenerators | null {
  return templateGenerators[templateId] || null;
}

export function generateResumeWithTemplate(
  templateId: string,
  format: 'pdf' | 'docx',
  resumeData: any
): Promise<void> | void {
  const generator = getTemplateGenerator(templateId);
  const safeResumeData = sanitizeResumeDataForDownload(resumeData);

  if (!generator) {
    // Fall back to professional-classic if template not found
    const fallback = templateGenerators['professional-classic'];
    return format === 'pdf' ? fallback.generatePDF(safeResumeData) : fallback.generateDOCX(safeResumeData);
  }

  return format === 'pdf' ? generator.generatePDF(safeResumeData) : generator.generateDOCX(safeResumeData);
}

export {
  generateProfessionalClassicDOCX,
  generateProfessionalClassicPDF,
  generateProfessionalClassicHTML,
  generateATSOptimizedDOCX,
  generateATSOptimizedPDF,
  generateATSOptimizedHTML,
  generateExecutiveDOCX,
  generateExecutivePDF,
  generateExecutiveHTML,
};
